import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { toast } from 'sonner';
import { Skeleton } from '@/components/atoms/skeleton';
import { CrewForm, type CrewFormValues } from '@/components/organisms/crews/CrewForm';
import { useApiMutation } from '@/hooks/useApiMutation';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useSchedulerCrews } from '@/hooks/scheduler/useSchedulerCrews';
import { useSchedulerTechnicians } from '@/hooks/scheduler/useSchedulerTechnicians';
import { apiGet, apiPatch, apiPost, operationsClient } from '@/lib/api';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { schedulerKeys } from '@/lib/queryKeys';
import type { SchedulerCrew } from '@/types/scheduler';

export const Route = createFileRoute('/_dashboard/dashboard/crews/$id/edit')({
  component: EditCrewPage,
});

function buildCrewFormValues(crew: SchedulerCrew): CrewFormValues {
  return {
    name: crew.name,
    timezone: crew.timezone,
    technicianIds: crew.technicians.map((technician) => technician.id),
    leadTechnicianId: crew.leadTechnicianId ?? null,
    color: crew.color ?? null,
    isActive: crew.isActive ?? true,
  };
}

function EditCrewSkeleton() {
  return (
    <div className="space-y-4 p-4">
      <Skeleton className="h-10 w-48" />
      <Skeleton className="h-40 w-full" />
      <Skeleton className="h-40 w-full" />
    </div>
  );
}

function EditCrewPage() {
  const { id } = Route.useParams();
  const router = useRouter();
  const queryClient = useQueryClient();
  const crewDetailKey = useMemo(() => [...schedulerKeys.crews(), id] as const, [id]);
  const crewsQuery = useSchedulerCrews();
  const techniciansQuery = useSchedulerTechnicians();

  const crewQuery = useApiQuery<SchedulerCrew>({
    queryKey: crewDetailKey,
    queryFn: () => apiGet<SchedulerCrew>(operationsClient, schedulerEndpoints.crew(id)),
    showErrorToast: false,
  });

  const updateMutation = useApiMutation<SchedulerCrew, CrewFormValues>({
    mutationFn: async (values) => {
      // 1. Base fields first (lead is set after membership is reconciled, since
      //    the backend requires the lead to be a current member).
      await apiPatch<SchedulerCrew>(operationsClient, schedulerEndpoints.crew(id), {
        name: values.name,
        timezone: values.timezone,
        color: values.color,
        isActive: values.isActive,
      });

      const currentMemberIds = new Set(
        (crewQuery.data?.technicians ?? []).map((technician) => technician.id),
      );
      const nextMemberIds = new Set(values.technicianIds);
      const toAssign = values.technicianIds.filter(
        (technicianId) => !currentMemberIds.has(technicianId),
      );
      const toUnassign = [...currentMemberIds].filter(
        (technicianId) => !nextMemberIds.has(technicianId),
      );

      await Promise.all([
        ...toAssign.map((technicianId) =>
          apiPost<void>(operationsClient, schedulerEndpoints.crewAssign(id), { technicianId }),
        ),
        ...toUnassign.map((technicianId) =>
          apiPost<void>(operationsClient, schedulerEndpoints.crewUnassign(id), { technicianId }),
        ),
      ]);

      // 2. Lead, now that members are reconciled.
      const crew = await apiPatch<SchedulerCrew>(operationsClient, schedulerEndpoints.crew(id), {
        leadTechnicianId: values.leadTechnicianId,
      });
      return crew;
    },
    onSuccess: () => {
      toast.success('Crew updated');
      void queryClient.invalidateQueries({ queryKey: schedulerKeys.crews() });
      void queryClient.invalidateQueries({ queryKey: schedulerKeys.technicians() });
      void queryClient.invalidateQueries({ queryKey: crewDetailKey });
      void router.navigate({
        to: '/dashboard/crews/$id',
        params: { id },
        search: true,
      });
    },
  });

  if (crewQuery.isLoading) return <EditCrewSkeleton />;

  if (!crewQuery.data) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-6 text-sm text-destructive">
        Crew not found.
      </div>
    );
  }

  return (
    <CrewForm
      mode="edit"
      initialValues={buildCrewFormValues(crewQuery.data)}
      currentCrewId={id}
      crews={crewsQuery.data ?? []}
      technicians={techniciansQuery.data ?? []}
      loadingOptions={crewsQuery.isLoading || techniciansQuery.isLoading}
      submitting={updateMutation.isPending}
      onBack={() =>
        void router.navigate({
          to: '/dashboard/crews/$id',
          params: { id },
          search: true,
        })
      }
      onSubmit={(values) => updateMutation.mutate(values)}
    />
  );
}
